import qs from 'qs';
import cookies from 'browser-cookies';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { devtoolsExchange } from '@urql/devtools';
import { retryExchange } from '@urql/exchange-retry';
import { cacheExchange } from '@urql/exchange-graphcache';
import { simplePagination } from '@urql/exchange-graphcache/extras';
import {
  createClient,
  dedupExchange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  debugExchange,
  fetchExchange,
  errorExchange,
  gql,
  ClientOptions,
} from 'urql';
import _ from 'lodash';
import { AutotagLevel } from '@scale/shared/nucleus/types';
import { getPageLoadSingleton } from '@scale/frontend-lib/globalSingletons';
import { NucleusGroundTruthEdit } from 'react-scale/generated/graphql_types';
import { GraphQLError, CustomerError } from '@scale/shared/nucleus/errors';
import logger from '@scale/frontend-lib/logging';
import { handleGraphQLError } from './errorHandling';
import { NucleusModelListDocument } from 'nucleus/hooks/queries/useNucleusModelList';
import { NucleusDatasetListDocument } from 'nucleus/hooks/queries/useNucleusDatasetList';
import { NucleusDatasetItemDetailDocument } from 'nucleus/hooks/queries/useNucleusDatasetItemDetail';
import { NucleusDatasetSchemaDocument } from 'nucleus/hooks/queries/useNucleusDatasetInfo';

function getRequestUrl() {
  const baseUrl = '/internal/nucleus/graphql';

  if (typeof window === 'undefined') {
    return baseUrl;
  }

  const searchParams: Record<string, any> = {
    pageLoadId: getPageLoadSingleton(),
  };
  const queryParams = new URLSearchParams(window.location.search);
  if (queryParams.get('spoof')) {
    searchParams.spoof = queryParams.get('spoof');
  }

  return `${baseUrl}?${qs.stringify(searchParams)}`;
}

const NucleusDashboardById = gql`
  query NucleusDashboardById($dashboardId: ID!) {
    nucleusDashboardById(dashboardId: $dashboardId) {
      name
      isPublic
      layout {
        i
        refreshedAt
        result
      }
    }
  }
`;

const cache = cacheExchange({
  keys: {
    NucleusGroundTruthEdit: (data: NucleusGroundTruthEdit) => data.edit_id,
    NucleusDatasetSchemaType: () => null,
    NucleusAnnotationsByGeometrySchemaType: () => null,
    NucleusKeyValuePairFloat: () => null,
    NucleusImageSize: () => null,
    NucleusKeyValuePair: () => null,
    NucleusXY: () => null,
    NucleusXYZ: () => null,
    NucleusSegmentation: () => null,
    NucleusBaseSegment: () => null,
    NucleusSegmentationPrediction: () => null,
    NucleusAnnotationPredictionSegmentation: () => null,
    NucleusAnnotationPrediction: () => null,
    NucleusTable: () => null,
    NucleusFieldParams: () => null,
    NucleusAutotagRefinement: () => null,
    BillingKeyValuePair: () => null,
    MonthYear: () => null,
    CameraGridTransformType: () => null,
    CameraTransformParams: () => null,
    NucleusMultiDatasetItemList: () => null,
    NucleusMultiDatasetNaturalLanguageItemList: () => null,
    // urql TypeScript is wrong
  } as any,
  resolvers: {
    NucleusDataset: {
      itemListInfinite: simplePagination({
        offsetArgument: 'offset',
        limitArgument: 'limit',
        mergeMode: 'after',
      }),
    },
    ModelCiTestEvaluation: {
      paginatedItemList: simplePagination({
        offsetArgument: 'offset',
        limitArgument: 'limit',
        mergeMode: 'after',
      }),
      paginatedSceneList: simplePagination({
        offsetArgument: 'offset',
        limitArgument: 'limit',
        mergeMode: 'after',
      }),
      paginatedTrackList: simplePagination({
        offsetArgument: 'offset',
        limitArgument: 'limit',
        mergeMode: 'after',
      }),
    },
    ModelCiUnitTest: {
      paginatedItemList: simplePagination({
        offsetArgument: 'offset',
        limitArgument: 'limit',
        mergeMode: 'after',
      }),
      paginatedSceneList: simplePagination({
        offsetArgument: 'offset',
        limitArgument: 'limit',
        mergeMode: 'after',
      }),
      paginatedTrackList: simplePagination({
        offsetArgument: 'offset',
        limitArgument: 'limit',
        mergeMode: 'after',
      }),
    },
  },
  updates: {
    Mutation: {
      nucleusDatasetCreate(result, args, cache) {
        // Finds all of nucleusDatasets query and updates using the proper variables
        cache
          .inspectFields('Query')
          .filter(field => field.fieldName === 'nucleusDatasets')
          .forEach(field => {
            cache.updateQuery(
              {
                query: NucleusDatasetListDocument,
                variables: {
                  offset: field?.arguments?.offset,
                  limit: field?.arguments?.limit,
                  query: field?.arguments?.query,
                  privacyType: field?.arguments?.privacyType,
                },
              },
              data => {
                if (!data) return null;
                if (!result.nucleusDatasetCreate) {
                  return data;
                }
                const dataCopy = data;
                (dataCopy.nucleusDatasets as any)?.unshift(result.nucleusDatasetCreate);
                dataCopy.nucleusDatasetsCount =
                  ((dataCopy.nucleusDatasetsCount as number) || 0) + 1;
                return dataCopy;
              },
            );
          });
      },
      nucleusIngestTasks(result, args, cache) {
        // Same concept as nucleusDatasetCreate
        cache
          .inspectFields('Query')
          .filter(field => field.fieldName === 'nucleusDatasets')
          .forEach(field => {
            cache.updateQuery(
              {
                query: NucleusDatasetListDocument,
                variables: {
                  offset: field?.arguments?.offset,
                  limit: field?.arguments?.limit,
                  query: field?.arguments?.query,
                  privacyType: field?.arguments?.privacyType,
                },
              },
              data => {
                if (!data) return null;
                if (!result.nucleusIngestTasks) {
                  return data;
                }
                (data.nucleusDatasets as any)?.unshift(result.nucleusIngestTasks);
                return data;
              },
            );
          });
      },
      nucleusForkDataset(result, args, cache) {
        // Same concept as nucleusDatasetCreate
        cache
          .inspectFields('Query')
          .filter(field => field.fieldName === 'nucleusDatasets')
          .forEach(field => {
            cache.updateQuery(
              {
                query: NucleusDatasetListDocument,
                variables: {
                  offset: field?.arguments?.offset,
                  limit: field?.arguments?.limit,
                  query: field?.arguments?.query,
                  privacyType: field?.arguments?.privacyType,
                },
              },
              data => {
                if (!data) return null;
                if (!result.nucleusForkDataset) {
                  return data;
                }
                (data.nucleusDatasets as any)?.unshift(result.nucleusForkDataset);
                return data;
              },
            );
          });
      },
      nucleusModelProjectCreate(result, args, cache) {
        // Finds all of nucleusModelProjects query and updates using the proper variables
        cache
          .inspectFields('Query')
          .filter(field => field.fieldName === 'nucleusModelProjects')
          .forEach(field => {
            cache.updateQuery(
              {
                query: NucleusModelListDocument,
                variables: { limit: field?.arguments?.limit },
              },
              data => {
                if (!data) return null;
                if (!result.nucleusModelProjectCreate) {
                  return data;
                }
                (data?.nucleusModelProjects as any)?.unshift(result.nucleusModelProjectCreate);
                return data;
              },
            );
          });
      },
      nucleusSliceCreateFromIds(result, args, cache) {
        // Invalidates the entire Dataset so it can have up to date slices in the slice page and invalidates the schema to contain the new slice
        // Seems like GQL won't allow to just invalidate the key here (like 'slices') as it will show the previously cache paginated query
        // We could use the same concept as the nucleusDatasetCreate mutation in this file, the problem is that the main query is still nucleusDatasetBy id, so it's impossible to get the slice paginated list query variables
        // Invalidating the whole dataset seems the best way to keep data updated here
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
        });
        // Updates the schema to contain the new slice ids
        cache.updateQuery(
          { query: NucleusDatasetSchemaDocument, variables: { datasetId: args.datasetId } },
          data => {
            if (!data) return null;
            if (!result.nucleusSliceCreateFromIds) {
              return data;
            }
            (data.nucleusDatasetById as any).schema.slices.unshift(
              result.nucleusSliceCreateFromIds as any,
            );
            return data;
          },
        );
      },
      nucleusSliceCreateFromQuery(result, args, cache) {
        // Same concept as nucleusSliceCreateFromIds
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
        });
        // Updates the schema to contain the new slice ids
        cache.updateQuery(
          { query: NucleusDatasetSchemaDocument, variables: { datasetId: args.datasetId } },
          data => {
            if (!data) return null;
            if (!result.nucleusSliceCreateFromQuery) {
              return data;
            }
            (data.nucleusDatasetById as any).schema.slices.unshift(
              result.nucleusSliceCreateFromQuery as any,
            );
            return data;
          },
        );
      },
      nucleusSliceBuild(result, args, cache) {
        // Same concept as nucleusSliceCreateFromIds
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
        });
        // Updates the schema to contain the new slice ids
        cache.updateQuery(
          { query: NucleusDatasetSchemaDocument, variables: { datasetId: args.datasetId } },
          data => {
            if (!data) return null;
            if (!result.nucleusSliceBuild) {
              return data;
            }
            (data.nucleusDatasetById as any).schema.slices.unshift(result.nucleusSliceBuild as any);
            return data;
          },
        );
      },
      nucleusSliceAppendIds(result, args, cache) {
        // Same concept as nucleusSliceCreateFromIds
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
        });
        // Here we also have to invalidate the slice
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
          sliceId: args.sliceId as string,
        });
        cache.updateQuery(
          { query: NucleusDatasetSchemaDocument, variables: { datasetId: args.datasetId } },
          data => {
            if (!data) return null;
            if (!result.nucleusSliceAppendIds) {
              return data;
            }
            (data.nucleusDatasetById as any).schema.slices.unshift(
              result.nucleusSliceAppendIds as any,
            );
            return data;
          },
        );
      },
      nucleusSliceAppendWithQuery(result, args, cache) {
        // Same concept as nucleusSliceAppendIds
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
        });
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
          sliceId: args.sliceId as string,
        });
        cache.updateQuery(
          { query: NucleusDatasetSchemaDocument, variables: { datasetId: args.datasetId } },
          data => {
            if (!data) return null;
            if (!result.nucleusSliceAppendWithQuery) {
              return data;
            }
            (data.nucleusDatasetById as any).schema.slices.unshift(
              result.nucleusSliceAppendWithQuery as any,
            );
            return data;
          },
        );
      },
      nucleusUpdateMetadata(result, args, cache) {
        // Update the NucleusItemDetail query based on the dataset id
        cache.updateQuery(
          {
            query: NucleusDatasetItemDetailDocument,
            variables: { datasetItemId: args.datasetItemId },
          },
          data => {
            if (!data) return null;
            if (!result.nucleusUpdateMetadata) {
              return data;
            }
            (data?.nucleusDatasetItemDetailById as any).metadata.unshift(
              result.nucleusUpdateMetadata,
            );
            return data;
          },
        );
        // Invalidate item coordinate used by charts so it refetches only when metadata changes
        cache.invalidate(
          { __typename: 'NucleusDatasetItem', id: args.datasetItemId as string },
          'coordinate' as any,
        );
        // Update the NucleusDatasetSchema query based on the dataset id
        cache.updateQuery(
          { query: NucleusDatasetSchemaDocument, variables: { datasetId: args.datasetId } },
          data => {
            if (!data) return null;
            if (!result.nucleusUpdateMetadata) {
              return data;
            }
            (data.nucleusDatasetById as any).schema.metadata.unshift(
              result.nucleusUpdateMetadata as any,
            );
            return data;
          },
        );
      },
      nucleusUpdateSceneMetadata(result, args, cache) {
        // Update the NucleusItemDetail query based on the dataset id
        cache.updateQuery(
          {
            query: NucleusDatasetItemDetailDocument,
            variables: { datasetItemId: args.datasetItemId },
          },
          data => {
            if (!data) return null;
            if (!result.nucleusUpdateSceneMetadata) {
              return data;
            }
            (data?.nucleusDatasetItemDetailById as any).metadata.unshift(
              result.nucleusUpdateSceneMetadata,
            );
            return data;
          },
        );
        // Update the NucleusDatasetSchema query based on the dataset id
        cache.updateQuery(
          { query: NucleusDatasetSchemaDocument, variables: { datasetId: args.datasetId } },
          data => {
            if (!data) return null;
            if (!result.nucleusUpdateSceneMetadata) {
              return data;
            }
            (data.nucleusDatasetById as any).schema.metadata.unshift(
              result.nucleusUpdateSceneMetadata as any,
            );
            return data;
          },
        );
      },
      nucleusSaveAutotagConfig(result, args, cache) {
        // Same concept as nucleusSliceCreateFromIds
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
        });
      },
      nucleusEditAnnotations(result, args, cache) {
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
        });
      },
      nucleusCreateAutotag(result, args, cache) {
        // Same concept as nucleusSliceCreateFromIds
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
        });
        // Updates the schema to contain the new autotags ids
        cache.updateQuery(
          { query: NucleusDatasetSchemaDocument, variables: { datasetId: args.datasetId } },
          data => {
            if (!data) return null;
            if (!result.nucleusCreateAutotag) {
              return data;
            }
            if ((result.nucleusCreateAutotag as any).autotag_level === AutotagLevel.Image) {
              (data.nucleusDatasetById as any).schema.autotag.unshift(
                result.nucleusCreateAutotag as any,
              );
            } else {
              (data.nucleusDatasetById as any).schema.autotagObject.unshift(
                result.nucleusCreateAutotag as any,
              );
            }
            return data;
          },
        );
      },
      nucleusSliceRemoveIds(result, args, cache) {
        // Invalidates the entire NucleusSlice so it can show up to date items in the grid
        // Since this can be a list of deleted items it's better to just clear the Slice
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
          sliceId: args.sliceId,
        });
        // Invalidates the NucleusDataset so it can show up to date slice counts
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
        });
      },
      nucleusDatasetItemDelete(result, args, cache) {
        // Invalidates the entire Dataset so it can show up to date items in the grid
        // Since this can be a list of deleted items it's better to just clear the Dataset
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
        });
      },
      nucleusUpdateColorScheme(result, args, cache) {
        // Invalidates the entire Dataset so it can show up to annotation colors
        cache.invalidate({
          __typename: 'NucleusDataset',
          id: args.datasetId as string,
        });
      },
      nucleusAutotagDelete(result, args, cache) {
        // Clears the NucleusAutotag based on it's id, the mutation needs to return the correct typename and id
        // This is what we are passing to the invalidation:
        // {
        //   __typename: 'NucleusAutotag',
        //   id: autotagId
        // }
        if (result.nucleusAutotagDelete) {
          cache.invalidate(result.nucleusAutotagDelete as any);
        }
      },
      nucleusSliceDelete(result, args, cache) {
        // Clears the NucleusSlice based on it's id, the mutation needs to return the correct typename and id
        // This is what we are passing to the invalidation:
        // {
        //   __typename: 'NucleusSlice',
        //   id: sliceId
        // }
        if (result.nucleusSliceDelete) {
          cache.invalidate(result.nucleusSliceDelete as any);
        }
      },
      nucleusRenameSlice(result, args, cache) {
        // Same as above
        if (result.nucleusRenameSlice) {
          cache.invalidate(result.nucleusRenameSlice as any);
        }
      },
      nucleusChangeSliceDescription(result, args, cache) {
        // Same as above
        if (result.nucleusChangeSliceDescription) {
          cache.invalidate(result.nucleusChangeSliceDescription as any);
        }
      },
      nucleusDatasetDelete(result, args, cache) {
        // Clears the NucleusDataset based on it's id, the mutation needs to return the correct typename and id
        // This is what we are passing to the invalidation:
        // {
        //   __typename: 'NucleusDataset',
        //   id: datasetId
        // }
        if (result.nucleusDatasetDelete) {
          cache.invalidate(result.nucleusDatasetDelete as any);
        }
      },
      nucleusToggleModelArchive(result, args, cache) {
        // Clears the NucleusDatasetProjectModel based on it's id, the mutation needs to return the correct typename and id
        // This is what we are passing to the invalidation:
        // {
        //   __typename: 'NucleusDatasetProjectModel',
        //   id: datasetId
        // }
        cache.invalidate(result.nucleusToggleModelArchive as any);
      },
      nucleusRemoveTagFromModelProject(result, args, cache) {
        // Finds all of nucleusModelProjects query and updates using the proper variables
        cache
          .inspectFields('Query')
          .filter(field => field.fieldName === 'nucleusModelProjects')
          .forEach(field => {
            cache.updateQuery(
              {
                query: NucleusDatasetListDocument,
                variables: {
                  limit: field?.arguments?.limit,
                },
              },
              data => {
                if (!data) return null;
                if (!result.nucleusRemoveTagFromModelProject) {
                  return data;
                }
                (data.nucleusModelProjects as any)?.unshift(
                  result.nucleusRemoveTagFromModelProject,
                );
                return data;
              },
            );
          });
      },
      nucleusAddTagsToModelProject(result, args, cache) {
        // Same as above
        cache
          .inspectFields('Query')
          .filter(field => field.fieldName === 'nucleusModelProjects')
          .forEach(field => {
            cache.updateQuery(
              {
                query: NucleusDatasetListDocument,
                variables: {
                  limit: field?.arguments?.limit,
                },
              },
              data => {
                if (!data) return null;
                if (!result.nucleusRemoveTagFromModelProject) {
                  return data;
                }
                (data.nucleusModelProjects as any)?.unshift(
                  result.nucleusRemoveTagFromModelProject,
                );
                return data;
              },
            );
          });
      },
      nucleusUpdateDashboard(result, args, cache) {
        const NucleusDashboardList = gql`
          query NucleusDashboardList {
            nucleusDashboardList {
              name
              isPublic
              layout {
                i
                refreshedAt
                result
              }
            }
          }
        `;
        cache.updateQuery(
          { query: NucleusDashboardById, variables: { dashboardId: args.dashboardId } },
          data => {
            if (!data) return null;
            if (!data.nucleusDashboardById) {
              return data;
            }
            data.nucleusDashboardById.name = args.name || data.nucleusDashboardById.name;
            data.nucleusDashboardById.layout = args.layout ?? data.nucleusDashboardById.layout;
            data.nucleusDashboardById.isPublic =
              args.isPublic || data.nucleusDashboardById.isPublic;
            return data;
          },
        );
        cache.updateQuery({ query: NucleusDashboardList }, data => {
          if (!data) return null;
          if (!data.nucleusDashboardList) {
            return data;
          }
          for (const dashboard of data.nucleusDashboardList as any[]) {
            dashboard.name = args.name || dashboard.name;
            dashboard.isPublic = args.isPublic ?? dashboard.isPublic;
            dashboard.layout = args.layout || dashboard.layout;
          }
          return data;
        });
      },
      modelCiUnitTestCreate(result, args, cache) {
        const ModelCiUnitTestsQuery = gql`
          query ModelCiUnitTestsQuery {
            modelCiUnitTests {
              id
            }
          }
        `;
        cache.updateQuery({ query: ModelCiUnitTestsQuery, variables: {} }, data => {
          if (!data) {
            return null;
          }
          if (!result.modelCiUnitTestCreate) {
            return data;
          }
          data.modelCiUnitTests.unshift(result.modelCiUnitTestCreate);
          return data;
        });
      },
    },
  },
});

export function createGQLClient(options: Partial<ClientOptions> = {}) {
  return createClient({
    url: getRequestUrl(),
    exchanges: [
      errorExchange({
        onError: (error, op) => {
          const rawErrorMessage = error.message;
          const gqlErrorMessage = GraphQLError.parseMessage(rawErrorMessage);
          const custErrorMessage = CustomerError.parseMessage(gqlErrorMessage);
          if (!custErrorMessage) {
            logger.error(gqlErrorMessage || rawErrorMessage, op as any);
          }
        },
      }),
      dedupExchange,
      cache,
      retryExchange({
        maxNumberAttempts: 1,
      }),
      // import from @scale/shared/environment
      // ...(isDevelopment ? [devtoolsExchange, debugExchange] : []),
      fetchExchange,
    ],
    fetch: (...args) =>
      fetch(...args).then(response => {
        if (!response.ok) {
          handleGraphQLError(response);
        }
        return response;
      }),
    fetchOptions: () => {
      return {
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': cookies.get('_csrf') ?? '',
          // Include full uri as referer so we know where the gql came from.
          Referer: window.location.href,
        },
      };
    },
    ...options,
  });
}

export const graphqlClient = createGQLClient();

export default graphqlClient;

export function isSamePaginatedQuery<T extends object>(variables: T, queryVariables?: T): boolean {
  if (!queryVariables) {
    return false;
  }
  return _.isEqual(_.omit(variables, 'offset'), _.omit(queryVariables, 'offset'));
}
